/**
 * The masthead menu: a panel that slides in from the trailing edge.
 *
 * Opened by the hamburger on the AppHeader, so it is reachable from every
 * authenticated screen. It holds the account-wide destinations that have no home
 * in the bottom nav — the nav is four tabs of task surfaces (home, activity,
 * receive, send) and cannot grow — plus Sign out, which is an account action
 * rather than a destination and so sits apart at the foot of the panel.
 *
 * DESTINATIONS ARE DATA. The rows come from one array below, so adding or
 * reordering a menu entry is a one-line edit and every row is guaranteed the same
 * markup, focus behaviour, and close-on-navigate.
 *
 * MODALITY. The panel is a real modal dialog: it dims the page, traps focus, and
 * restores focus to the hamburger on close (useDialogTrap, the same hook the
 * passkey and second-account prompts use). Escape closes it, as does a click on
 * the backdrop — the backdrop is a real button so the dismiss is keyboard
 * reachable rather than mouse-only.
 *
 * The slide is native CSS, matching the bottom sheet's approach: this app carries
 * no animation library, so motion is expressed in the stylesheet and the panel
 * only owns the open/closed state.
 */

import { useRef } from 'react';
import { Link } from 'react-router-dom';

import { useDialogTrap } from '@/components/useDialogTrap';
import { logout } from '@/lib/auth';
import { ROUTE_LANGUAGE_APPEARANCE, ROUTE_SECURITY, ROUTE_DEVELOPER } from '@/lib/routes';
import { useT } from '@/i18n';
import type { MessageKey } from '@/i18n/en';

interface MenuDrawerProps {
  /** Close the panel. The caller owns the open state. */
  onClose: () => void;
}

/** One navigable row in the panel. */
interface MenuEntry {
  path: string;
  labelKey: MessageKey;
  icon: IconName;
}

type IconName = 'approvals' | 'security' | 'appearance' | 'developer';

/**
 * The menu's destinations, in order. Every label reuses the key its own screen
 * already ships, so a row can never drift from the title of the page it opens.
 *
 * These four ARE the old /settings page. That hub listed the same sections as
 * headings on one long scroll, behind a row in this very menu — a screen whose
 * only job was to hold a list that this panel can hold directly. The hub is gone
 * and each section is now a destination of its own.
 */
const ENTRIES: MenuEntry[] = [
  { path: '/approvals', labelKey: 'approvals.title', icon: 'approvals' },
  { path: ROUTE_SECURITY, labelKey: 'settings.security.title', icon: 'security' },
  { path: ROUTE_LANGUAGE_APPEARANCE, labelKey: 'preauth.menu.title', icon: 'appearance' },
  { path: ROUTE_DEVELOPER, labelKey: 'settings.devmode.title', icon: 'developer' },
];

/** Row glyphs, drawn inline so the menu needs no icon font or image fetch. */
const ICONS: Record<IconName, string[]> = {
  approvals: ['M9 12l2 2 4-4', 'M12 3l7 3v6c0 4.5-3 8.3-7 9-4-.7-7-4.5-7-9V6z'],
  security: ['M5 11h14v10H5z', 'M8 11V7a4 4 0 0 1 8 0v4'],
  appearance: ['M12 3v18', 'M12 3a9 9 0 0 1 0 18 9 9 0 0 1 0-18z'],
  developer: ['M8 8l-4 4 4 4', 'M16 8l4 4-4 4'],
};

/** A row glyph. Decorative: the row's text carries the accessible name. */
function MenuIcon({ name }: { name: IconName }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICONS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/** The close X on the panel header. */
function CloseGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

/** Render the slide-in menu. Mounted only while open, so the animation replays. */
export default function MenuDrawer({ onClose }: MenuDrawerProps) {
  const t = useT();
  const panelRef = useRef<HTMLDivElement | null>(null);
  useDialogTrap(panelRef, onClose);

  // logout clears the local session and dispatches the session-expired event that
  // routes back to sign-in, so this panel does not navigate itself — the same
  // fire-and-forget call Settings makes. Closing first keeps the panel from
  // lingering over the sign-in screen during the teardown.
  const signOut = () => {
    onClose();
    void logout();
  };

  return (
    <div className="menu-drawer-backdrop">
      <button
        type="button"
        className="menu-drawer-backdrop__dismiss"
        aria-label={t('menu.close')}
        onClick={onClose}
      />
      <div
        className="menu-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={t('menu.title')}
        ref={panelRef}
      >
        <div className="menu-drawer__head">
          <span className="menu-drawer__title">{t('menu.title')}</span>
          <button
            type="button"
            className="menu-drawer__close"
            aria-label={t('menu.close')}
            onClick={onClose}
          >
            <CloseGlyph />
          </button>
        </div>

        <nav className="menu-drawer__list" aria-label={t('menu.title')}>
          {ENTRIES.map((entry) => (
            <Link key={entry.path} className="menu-drawer__row" to={entry.path} onClick={onClose}>
              <span className="menu-drawer__row-icon" aria-hidden="true">
                <MenuIcon name={entry.icon} />
              </span>
              <span className="menu-drawer__row-label">{t(entry.labelKey)}</span>
            </Link>
          ))}
        </nav>

        {/* Sign out is an action, not a destination, so it sits below the list and
            away from it — a mis-tap here ends the session. */}
        <button type="button" className="menu-drawer__signout" onClick={signOut}>
          {t('settings.account.signout')}
        </button>
      </div>
    </div>
  );
}
